import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  createCatalogItem,
  deleteCatalogItem,
  getCatalogItems,
  updateCatalogItem,
} from "../../api";
import ManagementFilterHeader, { formatManagementSearchInput, matchesManagementSearch, ManagementListLoading, makeManagementFilterOptions, matchesManagementFilter, sortManagementRows } from "../management/ManagementFilterHeader";
import VestaSelect from "./VestaSelect";
import { OverlayFieldLabel } from "./OverlayValidation";
import ImportExportOverlay from "../management/ImportExportOverlay";
import useIncrementalRows from "../../hooks/useIncrementalRows";
import { getUserPageLevel, getUserTableLevel, readStoredUser, UNAUTHORIZED_LABEL } from "../../utils/permissions";

const DEFAULT_NOTE_ENDPOINT = "default-notes";
const EMPTY_ITEM = { description: "", noteType: "", active: true, __v: 0 };
const EMPTY_FILTERS = { description: [], noteType: [] };
const NOTE_TYPE_OPTIONS = [
  { value: "Cliente", label: "Cliente" },
  { value: "Prenotazione", label: "Prenotazione" },
  { value: "Documento", label: "Documento" },
  { value: "Servizio", label: "Servizio" },
];
const NOTE_TYPE_VALUES = new Set(NOTE_TYPE_OPTIONS.map((option) => option.value));
const IMPORT_EXPORT_FIELDS = [
  { key: "description", label: "Descrizione", required: true },
  { key: "noteType", label: "Tipo Nota", required: true },
  { key: "active", label: "Attivo", type: "boolean", aliases: ["Stato"] },
];

function cleanText(value) {
  return String(value || "").trim();
}

function normalizeNoteType(value) {
  const raw = cleanText(value).toLocaleLowerCase("it");
  return NOTE_TYPE_OPTIONS.find((option) => option.value.toLocaleLowerCase("it") === raw)?.value || "";
}

function appendNotes(currentValue, notes) {
  const current = String(currentValue || "").trimEnd();
  const block = notes.map((note) => cleanText(note.description)).filter(Boolean).join("\n\n");
  if (!block) return currentValue || "";
  return `${current}${current ? "\n\n" : ""}${block}`;
}

function displayValue(value) {
  const text = String(value || "").trim();
  return text || "-";
}

function OptionsMenu({ open, menuRef, newDisabled, bulkDisabled, confirming, canEdit, canDelete, onBlocked, onToggle, onNew, onReset, onImport, onExport, onStartConfirm, onCancelConfirm, onConfirm }) {
  return (
    <div className="management_options_menu" ref={menuRef}>
      <button type="button" className="management_options_button" onClick={onToggle} aria-label="Opzioni tabelle" aria-expanded={open}><span></span><span></span><span></span></button>
      {open && (
        <div className="management_options_dropdown">
          <button type="button" className={`management_options_item${blockedActionClass(canEdit)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} disabled={newDisabled} onClick={canEdit ? onNew : onBlocked}>Nuovo Valore</button>
          <button type="button" className="management_options_item" onClick={onReset}>Azzera ricerca e filtri</button>
          <button type="button" className={`management_options_item${blockedActionClass(canEdit)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} disabled={newDisabled} onClick={canEdit ? onImport : onBlocked}>Importa Valori</button>
          <button type="button" className="management_options_item" disabled={newDisabled} onClick={onExport}>Esporta Valori</button>
          {confirming ? (
            <div className="management_options_confirm_row">
              <button type="button" className="management_options_item is_neutral" onClick={onCancelConfirm}>Annulla</button>
              <button type="button" className={`management_options_item is_danger${blockedActionClass(canDelete)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={canDelete ? onConfirm : onBlocked}>Conferma</button>
            </div>
          ) : (
            <button type="button" className={`management_options_item is_danger${blockedActionClass(canDelete)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} disabled={bulkDisabled} onClick={canDelete ? onStartConfirm : onBlocked}>Eliminazione Multipla</button>
          )}
        </div>
      )}
    </div>
  );
}

function CatalogOverlay({ draft, error, onChange, onClose, onSave }) {
  return (
    <div className="overlay_backdrop">
      <div className="overlay_card booking_overlay_card" role="dialog" aria-modal="true" aria-label="Catalogo">
        <div className="overlay_header">
          <h2>{draft._id ? "MODIFICA VALORE" : "NUOVO VALORE"}</h2>
          <button type="button" className="overlay_close_button" onClick={onClose} aria-label="Chiudi">×</button>
        </div>
        <div className="overlay_body">
          <div className="overlay_section">
            <h3>Nota predefinita</h3>
            <div className="overlay_fields_grid">
              <label className="overlay_field"><OverlayFieldLabel required>Descrizione</OverlayFieldLabel><input value={draft.description || ""} onChange={(event) => onChange("description", event.target.value)} /></label>
              <label className="overlay_field"><OverlayFieldLabel required>Tipo Nota</OverlayFieldLabel><VestaSelect value={draft.noteType || ""} options={NOTE_TYPE_OPTIONS} placeholder="Seleziona tipo nota" onValueChange={(nextValue) => onChange("noteType", nextValue)} aria-label="Tipo Nota" /></label>
            </div>
          </div>
        </div>
        <div className="overlay_footer">
          <div className="overlay_error_message">{error}</div>
          <button type="button" className="overlay_save_button overlay_save_close_button" onClick={onSave}>Salva e Chiudi</button>
        </div>
      </div>
    </div>
  );
}

function DefaultNotePickerOverlay({ value, noteType = "", onApply, onClose }) {
  const contextualNoteType = normalizeNoteType(noteType);
  const initialNoteTypeFilter = contextualNoteType;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [noteTypeFilter, setNoteTypeFilter] = useState(initialNoteTypeFilter);
  const [draft, setDraft] = useState(EMPTY_ITEM);
  const [showEditor, setShowEditor] = useState(false);
  const [editorError, setEditorError] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState("");
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [activeFilter, setActiveFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState(EMPTY_FILTERS);
  const [sortState, setSortState] = useState({ key: "description", direction: "asc" });
  const [importExportMode, setImportExportMode] = useState("");
  const optionsMenuRef = useRef(null);
  const filterPanelRef = useRef(null);

  const storedUser = readStoredUser();
  const tablePageLevel = getUserPageLevel(storedUser, "table");
  const defaultNoteLevel = Math.max(1, getUserTableLevel(storedUser, "defaultNote"));
  const canEdit = tablePageLevel >= 1 && defaultNoteLevel >= 2;
  const canDelete = tablePageLevel >= 1 && defaultNoteLevel >= 3;
  const blockAction = () => {};

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getCatalogItems(DEFAULT_NOTE_ENDPOINT, { includeInactive: true });
      setItems((Array.isArray(data?.items) ? data.items : []).filter((item) => NOTE_TYPE_VALUES.has(normalizeNoteType(item.noteType))));
    } catch (err) {
      setError(err?.response?.data?.error || "Impossibile caricare le note predefinite");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    if (!optionsOpen) return undefined;
    function closeOptions(event) {
      if (optionsMenuRef.current && !optionsMenuRef.current.contains(event.target)) setOptionsOpen(false);
    }
    document.addEventListener("pointerdown", closeOptions);
    return () => document.removeEventListener("pointerdown", closeOptions);
  }, [optionsOpen]);

  useEffect(() => {
    if (!activeFilter) return undefined;
    function closeFilters(event) {
      if (filterPanelRef.current && !filterPanelRef.current.contains(event.target)) setActiveFilter("");
    }
    document.addEventListener("pointerdown", closeFilters);
    return () => document.removeEventListener("pointerdown", closeFilters);
  }, [activeFilter]);

  useEffect(() => {
    if (selectedIds.length === 0) setConfirmBulkDelete(false);
  }, [selectedIds]);

  const filterOptions = useMemo(() => ({
    description: makeManagementFilterOptions(items, (item) => item.description),
    noteType: makeManagementFilterOptions(items, (item) => item.noteType),
  }), [items]);
  const noteTypeSelectOptions = useMemo(() => [{ value: "", label: "Tutte le tipologie" }, ...NOTE_TYPE_OPTIONS], []);

  const filtered = useMemo(() => {
    return items.filter((item) => {
      const matchesSearch = matchesManagementSearch([item.description, item.noteType], search);
      if (!matchesSearch) return false;
      return matchesManagementFilter(item.noteType, columnFilters.noteType)
        && (!noteTypeFilter || normalizeNoteType(item.noteType) === normalizeNoteType(noteTypeFilter));
    });
  }, [items, search, columnFilters, noteTypeFilter]);

  const noteSortGetters = useMemo(() => ({
    description: (item) => item.description,
    noteType: (item) => item.noteType,
  }), []);
  const orderedFiltered = useMemo(() => sortManagementRows(filtered, sortState, noteSortGetters), [filtered, sortState, noteSortGetters]);
  const incrementalRows = useIncrementalRows(orderedFiltered);
  const filteredIds = orderedFiltered.map((item) => item._id);
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selectedIds.some((selectedId) => String(selectedId) === String(id)));
  const someFilteredSelected = filteredIds.some((id) => selectedIds.some((selectedId) => String(selectedId) === String(id)));
  const filterProps = { activeFilter, onToggle: toggleFilter, onCheckChange: updateCheckFilter, onClear: clearColumnFilter, onSort: toggleSort };

  function resetSearchAndFilters() {
    setSearch("");
    setColumnFilters(EMPTY_FILTERS);
    setSortState({ key: "description", direction: "asc" });
    setNoteTypeFilter(initialNoteTypeFilter);
    setFiltersExpanded(false);
    setActiveFilter("");
    setOptionsOpen(false);
    setConfirmBulkDelete(false);
  }

  function toggleFilter(columnKey) {
    setActiveFilter((current) => current === columnKey ? "" : columnKey);
  }

  function updateCheckFilter(columnKey, filterValue, checked) {
    setColumnFilters((current) => {
      const values = Array.isArray(current[columnKey]) ? current[columnKey] : [];
      return { ...current, [columnKey]: checked ? [...values, filterValue] : values.filter((item) => item !== filterValue) };
    });
  }

  function clearColumnFilter(columnKey) {
    setColumnFilters((current) => ({ ...current, [columnKey]: [] }));
  }

  function toggleSort(columnKey) {
    setSortState((current) => ({
      key: columnKey,
      direction: current.key === columnKey && current.direction === "asc" ? "desc" : "asc",
    }));
  }

  function toggleSelected(id, checked) {
    setSelectedIds((current) => checked ? [...new Set([...current, id])] : current.filter((item) => String(item) !== String(id)));
  }

  function toggleAllFiltered(checked) {
    const ids = orderedFiltered.map((item) => item._id);
    setSelectedIds((current) => checked
      ? [...new Set([...current, ...ids])]
      : current.filter((id) => !ids.some((itemId) => String(itemId) === String(id))));
  }

  function openItem(item = null) {
    if (!canEdit) {
      blockAction();
      return;
    }
    setEditorError("");
    setConfirmDeleteId("");
    setOptionsOpen(false);
    setConfirmBulkDelete(false);
    setDraft({ ...EMPTY_ITEM, ...(item || {}) });
    setShowEditor(true);
  }

  function updateDraft(field, fieldValue) {
    setDraft((current) => ({ ...current, [field]: fieldValue }));
  }

  async function saveItem() {
    if (!canEdit) {
      setEditorError(UNAUTHORIZED_LABEL);
      return;
    }
    setEditorError("");
    if (!cleanText(draft.description) || !cleanText(draft.noteType)) {
      setEditorError("Completa i Dati Richiesti");
      return;
    }
    try {
      const data = draft._id
        ? await updateCatalogItem(DEFAULT_NOTE_ENDPOINT, draft._id, draft)
        : await createCatalogItem(DEFAULT_NOTE_ENDPOINT, draft);
      const saved = data?.item;
      if (saved) {
        setItems((current) => current.some((item) => String(item._id) === String(saved._id))
          ? current.map((item) => String(item._id) === String(saved._id) ? saved : item)
          : [...current, saved]);
      }
      setShowEditor(false);
    } catch (err) {
      setEditorError(err?.response?.data?.error || "Dati Errati");
    }
  }

  async function toggleActive(item) {
    if (!canEdit) {
      blockAction();
      return;
    }
    try {
      const data = await updateCatalogItem(DEFAULT_NOTE_ENDPOINT, item._id, { ...item, active: item.active === false });
      const saved = data?.item;
      if (saved) setItems((current) => current.map((row) => String(row._id) === String(saved._id) ? saved : row));
    } catch (err) {
      setError(err?.response?.data?.error || "Impossibile aggiornare lo stato valore");
    }
  }

  async function removeItem(item) {
    if (!canDelete) {
      blockAction();
      setConfirmDeleteId("");
      return;
    }
    try {
      await deleteCatalogItem(DEFAULT_NOTE_ENDPOINT, item._id, item.__v);
      setItems((current) => current.filter((row) => String(row._id) !== String(item._id)));
      setSelectedIds((current) => current.filter((id) => String(id) !== String(item._id)));
      setConfirmDeleteId("");
    } catch (err) {
      setError(err?.response?.data?.error || "Impossibile eliminare il valore");
    }
  }

  async function removeSelected() {
    if (selectedIds.length === 0) return;
    if (!canDelete) {
      blockAction();
      setConfirmBulkDelete(false);
      return;
    }
    try {
      const itemsById = new Map(items.map((row) => [String(row._id), row]));
      await Promise.all(selectedIds.map((id) => deleteCatalogItem(DEFAULT_NOTE_ENDPOINT, id, itemsById.get(String(id))?.__v)));
      setItems((current) => current.filter((row) => !selectedIds.some((id) => String(id) === String(row._id))));
      setSelectedIds([]);
      setConfirmBulkDelete(false);
      setOptionsOpen(false);
    } catch (err) {
      setError(err?.response?.data?.error || "Impossibile eliminare i valori selezionati");
    }
  }

  function openImportExport(mode) {
    if (mode === "import" && !canEdit) {
      blockAction();
      return;
    }
    setImportExportMode(mode);
    setOptionsOpen(false);
    setConfirmBulkDelete(false);
  }

  async function importRows(records) {
    if (!canEdit) throw new Error(UNAUTHORIZED_LABEL);
    let imported = 0;
    for (const record of records) {
      try {
        const data = await createCatalogItem(DEFAULT_NOTE_ENDPOINT, { ...EMPTY_ITEM, ...record });
        const saved = data?.item;
        if (saved) {
          setItems((current) => current.some((item) => String(item._id) === String(saved._id))
            ? current.map((item) => String(item._id) === String(saved._id) ? saved : item)
            : [...current, saved]);
        }
        imported += 1;
      } catch (err) {
        throw new Error(`Riga ${imported + 2}: ${err?.response?.data?.error || "Dati Errati"}`);
      }
    }
    return { imported };
  }

  function saveAndClose() {
    const selectedSet = new Set(selectedIds.map(String));
    const notesToApply = items.filter((item) => item.active !== false && selectedSet.has(String(item._id)));
    if (notesToApply.length > 0) onApply(appendNotes(value, notesToApply));
    onClose();
  }

  const portalRoot = document.querySelector(".app_root") || document.body;

  return createPortal(
    <>
      <div className="overlay_backdrop default_note_backdrop">
        <div className="overlay_card booking_overlay_card default_note_overlay_card" role="dialog" aria-modal="true" aria-label="Note predefinite">
          <div className="overlay_header">
            <h2>NOTE PREDEFINITE</h2>
            <button type="button" className="overlay_close_button" onClick={onClose} aria-label="Chiudi" tabIndex={-1}>×</button>
          </div>

          <div className="overlay_body default_note_overlay_body">
            <div className="management_page management_shared_page tables_page has_note_type_field default_note_tables_management">
              <div className="management_list_panel">
                <div className={`booking_toolbar management_toolbar management_table_toolbar management_options_search_toolbar management_shared_toolbar management_note_filters_toolbar${filtersExpanded ? " is_filters_expanded" : ""}`}>
                  <OptionsMenu
                    open={optionsOpen}
                    menuRef={optionsMenuRef}
                    newDisabled={loading}
                    bulkDisabled={selectedIds.length === 0}
                    confirming={confirmBulkDelete}
                    canEdit={canEdit}
                    canDelete={canDelete}
                    onBlocked={blockAction}
                    onToggle={() => setOptionsOpen((current) => !current)}
                    onNew={() => openItem()}
                    onReset={resetSearchAndFilters}
                    onImport={() => openImportExport("import")}
                    onExport={() => openImportExport("export")}
                    onStartConfirm={() => setConfirmBulkDelete(true)}
                    onCancelConfirm={() => setConfirmBulkDelete(false)}
                    onConfirm={removeSelected}
                  />
                  <input className="management_search" value={search} onChange={(event) => setSearch(formatManagementSearchInput(event.target.value))} placeholder="Cerca note (es. Cliente, Prenotazione)" />
                  <button
                    type="button"
                    className="vesta_filters_toggle management_note_filters_toggle"
                    onClick={() => setFiltersExpanded((current) => !current)}
                    aria-expanded={filtersExpanded}
                    aria-label={filtersExpanded ? "Accorcia filtri note predefinite" : "Espandi filtri note predefinite"}
                    title={filtersExpanded ? "Accorcia filtri" : "Espandi filtri"}
                  >
                    <span className="vesta_filters_toggle_icon" aria-hidden="true"><span></span><span></span></span>
                  </button>
                  <div className="management_toolbar_results"><strong className="management_results_count">Risultati: {filtered.length}</strong></div>
                  <div className="management_note_extra_filters" aria-hidden={!filtersExpanded}>
                    <label className="management_inline_filter">
                      <span>Tipo Nota</span>
                      <VestaSelect value={noteTypeFilter} options={noteTypeSelectOptions} onValueChange={setNoteTypeFilter} aria-label="Filtra note per tipo nota" />
                    </label>
                  </div>
                </div>

                <div className="management_board management_table_board">
                  <div className="management_table_shell" role="table" aria-label="Elenco valori tabella" ref={incrementalRows.shellRef} onScroll={incrementalRows.handleScroll}>
                    <div className="management_table_header management_shared_table_header management_tables_table_header" role="row" ref={filterPanelRef}>
                      <div className="management_table_select_head" role="columnheader">
                        <input
                          type="checkbox"
                          checked={allFilteredSelected}
                          ref={(input) => { if (input) input.indeterminate = !allFilteredSelected && someFilteredSelected; }}
                          onChange={() => toggleAllFiltered(!someFilteredSelected)}
                          aria-label="Seleziona tutti i valori filtrati"
                        />
                      </div>
                      <ManagementFilterHeader columnKey="description" label="Descrizione" filterable={false} value={[]} sortDirection={sortState.key === "description" ? sortState.direction : ""} {...filterProps} />
                      <ManagementFilterHeader columnKey="noteType" label="Tipo Nota" options={filterOptions.noteType} value={columnFilters.noteType} sortDirection={sortState.key === "noteType" ? sortState.direction : ""} {...filterProps} />
                      <div className="management_table_actions_head" role="columnheader">Azioni</div>
                    </div>

                    {loading && <ManagementListLoading label="Caricamento Note Predefinite" />}
                    {!loading && error && <div className="management_table_visible_status is_error">{error}</div>}
                    {!loading && !error && filtered.length === 0 && <div className="management_table_visible_status">Nessun Risultato Trovato</div>}

                    <div className="management_table_body" role="rowgroup">
                      {incrementalRows.rows.map((item) => {
                        const isConfirming = String(confirmDeleteId) === String(item._id);
                        const isSelected = selectedIds.some((id) => String(id) === String(item._id));
                        return (
                          <article key={item._id} className={`management_table_row management_tables_table_row${item.active === false ? " is_inactive" : ""}${isSelected ? " is_selected" : ""}`} role="row">
                            <div className="management_table_cell management_table_select_cell" role="cell">
                              <input type="checkbox" checked={isSelected} onChange={(event) => toggleSelected(item._id, event.target.checked)} aria-label={`Seleziona valore ${item.description}`} />
                            </div>
                            <div className="management_table_cell management_table_title_cell" role="cell"><strong>{displayValue(item.description)}</strong></div>
                            <div className="management_table_cell" role="cell">{displayValue(item.noteType)}</div>
                            <div className="management_table_actions management_status_actions" role="cell">
                              <button type="button" className={`management_action_button ${item.active === false ? "management_action_success" : "management_action_danger"}${blockedActionClass(canEdit)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={() => toggleActive(item)}>{item.active === false ? "Attiva" : "Disattiva"}</button>
                              {isConfirming ? (
                                <>
                                  <button type="button" className="management_action_button management_action_cancel" onClick={() => setConfirmDeleteId("")}>Annulla</button>
                                  <button type="button" className={`management_action_button management_action_confirm${blockedActionClass(canDelete)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={() => removeItem(item)}>Conferma</button>
                                </>
                              ) : (
                                <>
                                  <button type="button" className={`management_action_button management_action_primary${blockedActionClass(canEdit)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={() => openItem(item)}>Modifica</button>
                                  <button type="button" className={`management_action_button management_action_danger${blockedActionClass(canDelete)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={() => canDelete ? setConfirmDeleteId(item._id) : blockAction()}>Elimina</button>
                                </>
                              )}
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="overlay_footer">
            <div className="overlay_error_message"></div>
            <button type="button" className="overlay_save_button overlay_save_close_button" disabled={loading} onClick={saveAndClose}>Salva e Chiudi</button>
          </div>
        </div>
      </div>

      {showEditor && <CatalogOverlay draft={draft} error={editorError} onChange={updateDraft} onClose={() => setShowEditor(false)} onSave={saveItem} />}
      {importExportMode && (
        <ImportExportOverlay
          mode={importExportMode}
          title="Nota predefinita"
          entityLabel="Tabelle"
          fileBaseName="vesta_default-notes"
          fields={IMPORT_EXPORT_FIELDS}
          rows={orderedFiltered}
          onClose={() => setImportExportMode("")}
          onImport={importRows}
        />
      )}
    </>,
    portalRoot
  );
}

export default function DefaultNoteTextarea({ value, onChange, rows, className = "", noteType = "", disabled = false }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className={`default_note_textarea_wrap ${className}`.trim()}>
        <textarea value={value || ""} rows={rows} disabled={disabled} onChange={(event) => !disabled && onChange(event.target.value)} />
        <button type="button" className="default_note_picker_button" disabled={disabled} onClick={() => !disabled && setOpen(true)} aria-label="Apri note predefinite" title="Note predefinite" tabIndex={-1}>
          <svg viewBox="0 0 24 8" aria-hidden="true" className="default_note_picker_icon">
            <circle cx="4" cy="4" r="2" /><circle cx="12" cy="4" r="2" /><circle cx="20" cy="4" r="2" />
          </svg>
        </button>
      </div>
      {open && !disabled && <DefaultNotePickerOverlay value={value || ""} noteType={noteType} onApply={onChange} onClose={() => setOpen(false)} />}
    </>
  );
}
